import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Row, Col, DatePicker, Select, Input, Button, Table, Tag, Space, Drawer, Typography, Pagination, message } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import request from '../../../utils/request';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

interface LogItem {
  id: number | string;
  tenant_id?: number | string;
  actor_id?: number | string;
  actor_role?: string;
  event_type: string;
  target_type?: string;
  target_id?: number | string;
  correlation_id?: string;
  severity?: 'info' | 'warn' | 'error';
  context?: any;
  created_at: string;
}

const severityColor = (s?: string) => (s === 'error' ? 'red' : s === 'warn' ? 'orange' : 'blue');

const AdminLogSystemPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<LogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [inspect, setInspect] = useState<LogItem | null>(null);
  const [userNameMap, setUserNameMap] = useState<Record<string, string>>({});

  const fetchLogs = async (params?: any) => {
    setLoading(true);
    try {
      const p: any = {
        page,
        page_size: pageSize,
      };
      const v = params || form.getFieldsValue();
      if (v.range && Array.isArray(v.range) && v.range.length === 2) {
        p.created_at_from = v.range[0]?.toISOString?.();
        p.created_at_to = v.range[1]?.toISOString?.();
      }
      if (v.module) p.module = v.module; // event_type 前缀
      if (v.event_type) p.event_type = v.event_type;
      if (v.target_type) p.target_type = v.target_type;
      if (v.target_id) p.target_id = v.target_id;
      if (v.actor_id) p.actor_id = v.actor_id;
      if (v.severity) p.severity = v.severity;
      if (v.keyword) p.keyword = v.keyword;

      // 预留统一接口：/api/v1/audit/logs/
      const resp = await request.get('/api/v1/audit/logs/', { params: p });
      const payload = resp?.data || {};
      const list: LogItem[] = Array.isArray(payload?.results) ? payload.results : (Array.isArray(payload) ? payload : []);
      setRows(list);
      setTotal(Number(payload?.count || list.length || 0));
    } catch (e) {
      message.error('加载日志失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 默认近7天
    const end = dayjs();
    const start = end.subtract(7, 'day');
    form.setFieldsValue({ range: [start, end], severity: undefined });
    // 首次加载
    fetchLogs({ range: [start, end] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  // 补全操作者用户名
  useEffect(() => {
    (async () => {
      try {
        const ids: string[] = [];
        (rows || []).forEach((r) => {
          const aid = r.actor_id as any;
          if (typeof aid === 'number' || (typeof aid === 'string' && aid.trim() !== '')) {
            const key = String(aid);
            if (!userNameMap[key]) ids.push(key);
          }
        });
        const uniq = Array.from(new Set(ids));
        if (uniq.length === 0) return;
        const resultPairs: Array<[string,string]> = [];
        for (const id of uniq) {
          try {
            const resp = await request.get(`/api/v1/users/${id}/`);
            const u = resp?.data || {};
            const name = u.username || u.name || `用户${id}`;
            resultPairs.push([id, name]);
          } catch {
            resultPairs.push([id, `用户${id}`]);
          }
        }
        setUserNameMap(prev => {
          const next = { ...prev } as Record<string,string>;
          resultPairs.forEach(([id, name]) => { next[id] = name; });
          return next;
        });
      } catch {}
    })();
  }, [rows]);

  const columns = useMemo(() => [
    { title: '时间', dataIndex: 'created_at', key: 'created_at', width: 180, render: (v: any) => v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-' },
    { title: '模块', dataIndex: 'event_type', key: 'module', width: 160, render: (v: string) => (v ? String(v).split('.')[0] : '-') },
    { title: '事件', dataIndex: 'event_type', key: 'event_type', width: 240 },
    { title: '对象', key: 'target', width: 220, render: (_: any, r: LogItem) => {
      const code = r?.context?.system_code || r?.context?.instruction_system_code;
      if (code) {
        return <Tag color="blue">{code}</Tag>;
      }
      return <span>{r.target_type || '-'}{r.target_id ? ` #${r.target_id}` : ''}</span>;
    } },
    { title: '操作人', key: 'actor', width: 160, render: (_: any, r: LogItem) => {
      const aid = r.actor_id != null ? String(r.actor_id) : '';
      const name = aid ? (userNameMap[aid] || `用户${aid}`) : '-';
      return (
        <Space size={4}>
          <span>{name}</span>
          {r.actor_role ? <Tag>{r.actor_role}</Tag> : null}
        </Space>
      );
    } },
    { title: '级别', dataIndex: 'severity', key: 'severity', width: 100, render: (v: any) => (
      <Tag color={severityColor(v)}>{v || 'info'}</Tag>
    ) },
    { title: '操作', key: 'ops', fixed: 'right' as const, width: 100, render: (_: any, r: LogItem) => (
      <Space size={4}>
        <Button size="small" type="link" onClick={() => setInspect(r)}>查看</Button>
      </Space>
    ) },
  ], [userNameMap]);

  return (
    <div className="list-page-container">
      <Card>
        {/* 顶部标题行移除，统一由 TabsManager 控制标签标题与浏览器标题 */}

        <div style={{ marginBottom: 16 }}>
          <Form form={form} layout="horizontal" onFinish={() => { setPage(1); fetchLogs(); }}>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="range" label="时间范围">
                  <RangePicker showTime allowClear style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Form.Item name="module" label="模块">
                  <Select allowClear placeholder="选择模块">
                    <Option value="instruction">指令</Option>
                    <Option value="billing">账单</Option>
                    <Option value="auth">认证</Option>
                    <Option value="warehouse">仓库</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="event_type" label="事件">
                  <Input placeholder="如 instruction.status_changed" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Form.Item name="target_type" label="对象类型">
                  <Input placeholder="如 instruction/invoice" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Form.Item name="target_id" label="对象ID">
                  <Input placeholder="ID" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Form.Item name="actor_id" label="操作人">
                  <Input placeholder="用户ID" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Form.Item name="severity" label="级别">
                  <Select allowClear placeholder="选择级别">
                    <Option value="info">info</Option>
                    <Option value="warn">warn</Option>
                    <Option value="error">error</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="keyword" label="关键字">
                  <Input placeholder="搜索上下文/备注" allowClear />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 12]}>
              <Col xs={24}>
                <Form.Item>
                  <Space>
                    <Button type="primary" icon={<SearchOutlined />} htmlType="submit" loading={loading}>搜索</Button>
                    <Button icon={<ReloadOutlined />} onClick={() => { form.resetFields(); setPage(1); fetchLogs({}); }}>重置</Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Table
            rowKey={(r) => String((r as any).id)}
            loading={loading}
            dataSource={rows}
            columns={columns}
            pagination={false}
            scroll={{ x: 1000 }}
            onRow={(record) => ({ onDoubleClick: () => setInspect(record as LogItem) })}
          />
        </div>

        <div className="flex justify-end items-center">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            showQuickJumper
            pageSizeOptions={[10, 20, 50, 100] as any}
            onChange={(p, ps) => { setPage(p); setPageSize(ps); }}
          />
        </div>

        <Drawer
          title={inspect ? `日志详情 #${inspect.id}` : '日志详情'}
          open={!!inspect}
          onClose={() => setInspect(null)}
          width={720}
        >
          {inspect && (
            <div>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <div>
                  <Text type="secondary">时间：</Text>
                  <Text>{dayjs(inspect.created_at).format('YYYY-MM-DD HH:mm:ss')}</Text>
                </div>
                <div>
                  <Text type="secondary">事件：</Text>
                  <Text>{inspect.event_type}</Text>
                </div>
                <div>
                  <Text type="secondary">模块：</Text>
                  <Text>{inspect.event_type ? String(inspect.event_type).split('.')[0] : '-'}</Text>
                </div>
                <div>
                  <Text type="secondary">对象：</Text>
                  <Text>{inspect.target_type || '-'}{inspect.target_id ? ` #${inspect.target_id}` : ''}</Text>
                </div>
                <div>
                  <Text type="secondary">操作人：</Text>
                  <Text>{inspect.actor_id ?? '-'}{inspect.actor_role ? ` (${inspect.actor_role})` : ''}</Text>
                </div>
                <div>
                  <Text type="secondary">级别：</Text>
                  <Tag color={severityColor(inspect.severity)}>{inspect.severity || 'info'}</Tag>
                </div>
                <div>
                  <Text type="secondary">Correlation ID：</Text>
                  <Text copyable>{inspect.correlation_id || '-'}</Text>
                </div>
                <div>
                  <Text type="secondary">上下文：</Text>
                  <pre style={{ background: '#fafafa', padding: 12, border: '1px solid #f0f0f0', borderRadius: 4, maxHeight: 360, overflow: 'auto' }}>
                    {JSON.stringify(inspect.context || {}, null, 2)}
                  </pre>
                </div>
              </Space>
            </div>
          )}
        </Drawer>
      </Card>
    </div>
  );
};

export default AdminLogSystemPage;


